import { AuthService } from './authService';
import {
  ShipmentLineDto,
  CreateShipmentLineRequest,
  UpdateShipmentLineRequest,
  SaveShipmentLinesRequest,
  ShipmentLineAggregates,
} from '../types/shipmentLine';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ShipmentLineService {
  /**
   * Hent alle ShipmentLines for en PListID og OrderNo
   */
  async getByPListOrder(pListId: number, orderNo: number): Promise<ShipmentLineDto[]> {
    const url = `${API_BASE_URL}/shipment-lines/plist/${pListId}/order/${orderNo}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch shipment lines: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Hent alle ShipmentLines for en PListID
   */
  async getByPListId(pListId: number): Promise<ShipmentLineDto[]> {
    const url = `${API_BASE_URL}/shipment-lines/plist/${pListId}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch shipment lines: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Hent en ShipmentLine basert på LineID
   */
  async getByLineId(lineId: string): Promise<ShipmentLineDto> {
    const url = `${API_BASE_URL}/shipment-lines/${lineId}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch shipment line: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Opprett en ny ShipmentLine
   */
  async create(request: CreateShipmentLineRequest): Promise<ShipmentLineDto> {
    const url = `${API_BASE_URL}/shipment-lines`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create shipment line: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Oppdater en ShipmentLine
   */
  async update(lineId: string, request: UpdateShipmentLineRequest): Promise<ShipmentLineDto> {
    const url = `${API_BASE_URL}/shipment-lines/${lineId}`;
    const response = await AuthService.authenticatedPut(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update shipment line: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Slett en ShipmentLine
   */
  async delete(lineId: string): Promise<void> {
    const url = `${API_BASE_URL}/shipment-lines/${lineId}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to delete shipment line: ${response.statusText}`);
    }
  }

  /**
   * Lagre alle ShipmentLines for en ordre (bulk save)
   * Oppretter nye, oppdaterer eksisterende, sletter fjernede
   */
  async saveAll(request: SaveShipmentLinesRequest): Promise<ShipmentLineDto[]> {
    const url = `${API_BASE_URL}/shipment-lines/save-all`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to save shipment lines: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Beregn aggregerte verdier fra ShipmentLines for en ordre
   */
  async getAggregates(pListId: number, orderNo: number): Promise<ShipmentLineAggregates> {
    const url = `${API_BASE_URL}/shipment-lines/plist/${pListId}/order/${orderNo}/aggregates`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch aggregates: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Beregn volum fra dimensjoner (cm → m³)
   */
  calculateVolume(height: number, width: number, length: number): number {
    return Math.round((height * width * length) / 1_000_000 * 1_000_000) / 1_000_000;
  }
}

export const shipmentLineService = new ShipmentLineService();
